import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import { AdBannerResponsive } from '../components/GoogleAdsense';
import ProductCarousel from '../components/affiliate/ProductCarousel';
import '../styles/how-to-use.css';

const HowToUsePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('start');

  useEffect(() => {
    document.title = '使い方ガイド | はしもと栄養パートナー';
    const tab = searchParams.get('tab');
    if (tab && ['start', 'features', 'help'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const tabs = [
    { id: 'start', label: 'はじめに', icon: '🌸' },
    { id: 'features', label: 'できること', icon: '✨' },
    { id: 'help', label: '困ったとき', icon: '💡' },
  ];

  return (
    <AppLayout>
      <PageTitle
        title="使い方ガイド"
        subtitle="つむぎの使い方、かんたんにまとめたよ"
        mascot={<img src="/tsumugi.png" alt="つむぎ" className="howto__mascot-image" />}
      />

      <div className="howto">
        {/* タブナビゲーション */}
        <div className="howto__tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`howto__tab ${activeTab === tab.id ? 'howto__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="howto__tab-icon">{tab.icon}</span>
              <span className="howto__tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* コンテンツ */}
        <div className="howto__content">

          {/* ===============================================
              タブ1: はじめに
              =============================================== */}
          {activeTab === 'start' && (
            <div className="howto__section">

              {/* チャットへの導線CTA */}
              <div className="howto__start-cta">
                <p className="howto__start-cta-title">🌸 まずは話しかけてみよう</p>
                <p className="howto__start-cta-text">
                  つむぎは橋本病のことを理解しているAIパートナーです。<br />
                  難しいことは何もないよ。ひとこと、話しかけてみてね。
                </p>
                <button
                  className="howto__start-cta-btn"
                  onClick={() => navigate('/chat')}
                >
                  💬 つむぎと話してみる →
                </button>
              </div>

              {/* つむぎってなに？ */}
              <div className="howto__card">
                <h2 className="howto__heading">🌸 つむぎってなに？</h2>
                <p className="howto__text">
                  橋本病の方のための栄養サポートアプリです。
                </p>
                <p className="howto__text">
                  AIパートナー「つむぎ」が、あなたの体調に合わせた食事アドバイスをお届けします。
                </p>
                <ul className="howto__tips-list">
                  <li>✅ 橋本病に配慮した栄養アドバイス</li>
                  <li>✅ 冷蔵庫の食材からレシピ生成</li>
                  <li>✅ 商品の成分表チェック</li>
                  <li>✅ 特売チラシから節約レシピ</li>
                  <li>✅ 24時間いつでも相談OK</li>
                </ul>
              </div>

              {/* 画面の見方 */}
              <div className="howto__card">
                <h2 className="howto__heading">🏠 画面の見方</h2>
                <p className="howto__text">
                  画面下のボタンから、各機能に移動できます。
                </p>
                <div className="howto__screenshot">
                  <img
                    src="/guide-screenshots/footer-nav.png"
                    alt="フッターナビゲーション"
                    className="howto__screenshot-image"
                    onError={(e) => e.target.parentElement.style.display = 'none'}
                  />
                </div>
                <table className="howto__table">
                  <thead>
                    <tr>
                      <th>アイコン</th>
                      <th>名前</th>
                      <th>機能</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>📝</td>
                      <td>相談</td>
                      <td>フォーム形式の詳細相談（4pt）</td>
                    </tr>
                    <tr>
                      <td>💬</td>
                      <td>チャット</td>
                      <td>つむぎとの気軽な会話（1pt）</td>
                    </tr>
                    <tr>
                      <td>📚</td>
                      <td>レシピ</td>
                      <td>レシピハブ・保存レシピ</td>
                    </tr>
                    <tr>
                      <td>👤</td>
                      <td>マイページ</td>
                      <td>履歴・設定・プラン変更</td>
                    </tr>
                  </tbody>
                </table>
                <p className="howto__text">
                  迷ったら「💬 チャット」からつむぎに聞いてみてね。
                </p>
              </div>

              {/* まずやっておくと便利なこと */}
              <div className="howto__card">
                <h2 className="howto__heading">⚙️ まずやっておくと便利なこと</h2>

                <div className="howto__sub-section">
                  <h3 className="howto__subheading">ステップ1</h3>
                  <p className="howto__text">
                    プロフィールを入力すると、より的確なアドバイスが届きます。
                  </p>
                  <button
                    className="howto__action-btn"
                    onClick={() => navigate('/mypage')}
                  >
                    プロフィールを設定する →
                  </button>
                </div>

                <div className="howto__sub-section">
                  <h3 className="howto__subheading">ステップ2</h3>
                  <p className="howto__text">
                    よく使う調味料やアレルギーを設定すると、あなた専用のレシピが提案されます。
                  </p>
                  <button
                    className="howto__action-btn"
                    onClick={() => navigate('/recipe-hub')}
                  >
                    レシピ設定をする →
                  </button>
                </div>

                <p className="howto__hint-text">
                  あとからいつでも変更できるよ。設定しなくても使えるから、気が向いたときで大丈夫。
                </p>
              </div>

            </div>
          )}

          {/* ===============================================
              タブ2: できること
              =============================================== */}
          {activeTab === 'features' && (
            <div className="howto__section">

              {/* 目次ナビ */}
              <div className="howto__quick-nav">
                <div className="howto__quick-nav-links">
                  <a href="#feat-chat" className="howto__quick-nav-link">つむぎと話す</a>
                  <a href="#feat-form" className="howto__quick-nav-link">フォームで相談</a>
                  <a href="#feat-recipe" className="howto__quick-nav-link">レシピを作る</a>
                  <a href="#feat-shopping" className="howto__quick-nav-link">買い物レシピ</a>
                  <a href="#feat-check" className="howto__quick-nav-link">成分チェック</a>
                  <a href="#feat-rest" className="howto__quick-nav-link">おやすみモード</a>
                </div>
              </div>

              {/* つむぎと話す */}
              <div className="howto__card" id="feat-chat">
                <h2 className="howto__heading">💬 つむぎと話す</h2>
                <p className="howto__text">
                  気軽に何でも話しかけてね。体調のこと、食事のこと、ちょっとした疑問でも。つむぎが答えるよ。
                </p>
                <div className="howto__screenshot">
                  <img
                    src="/guide-screenshots/chat-screen.png"
                    alt="チャット画面"
                    className="howto__screenshot-image"
                    onError={(e) => e.target.parentElement.style.display = 'none'}
                  />
                </div>
                <div className="howto__chat-examples">
                  <p className="howto__chat-examples-label">たとえば、こんなふうに：</p>
                  <div className="howto__chat-bubble" onClick={() => navigate('/chat?prompt=' + encodeURIComponent('最近だるくて、何食べたらいいかな'))} role="button" tabIndex={0}>
                    最近だるくて、何食べたらいいかな
                  </div>
                  <div className="howto__chat-bubble" onClick={() => navigate('/chat?prompt=' + encodeURIComponent('昆布って食べないほうがいい？'))} role="button" tabIndex={0}>
                    昆布って食べないほうがいい？
                  </div>
                  <div className="howto__chat-bubble" onClick={() => navigate('/chat?prompt=' + encodeURIComponent('今日しんどい...話聞いてほしい'))} role="button" tabIndex={0}>
                    今日しんどい...話聞いてほしい
                  </div>
                </div>
                <p className="howto__hint-text">うまく言葉にならなくても、短い一言でいいよ。</p>
                <ol className="howto__steps-list">
                  <li>フッターの「💬 チャット」をタップ</li>
                  <li>メッセージを入力して送信</li>
                  <li>つむぎがすぐに返信</li>
                </ol>
                <p className="howto__hint-text">1pt / 回</p>
                <button
                  className="howto__action-btn"
                  onClick={() => navigate('/chat')}
                >
                  つむぎと話す →
                </button>
              </div>

              {/* フォームで相談 */}
              <div className="howto__card" id="feat-form">
                <h2 className="howto__heading">📝 フォームで相談</h2>
                <p className="howto__text">
                  詳しく伝えたいときはフォーム相談がおすすめ。体調・食事内容・悩みをまとめて入力できます。
                </p>
                <p className="howto__text">
                  <strong>こんな時に：</strong>週間レシピがほしい / 血液検査の結果を見てほしい / 詳しいアドバイスがほしい
                </p>
                <ol className="howto__steps-list">
                  <li>フッターの「📝 相談」をタップ</li>
                  <li>体調・食事内容・悩みを入力</li>
                  <li>「送信」→ 10〜30秒で回答</li>
                </ol>
                <p className="howto__hint-text">4pt / 回</p>
                <button
                  className="howto__action-btn"
                  onClick={() => navigate('/form-v2')}
                >
                  フォームで相談する →
                </button>
              </div>

              {/* 今ある食材でレシピ */}
              <div className="howto__card" id="feat-recipe">
                <h2 className="howto__heading">🧊 今ある食材でレシピ</h2>
                <p className="howto__text">
                  冷蔵庫の食材から、橋本病に配慮したレシピを自動で作ります。写真を撮るか、食材を選ぶだけ。
                </p>
                <div className="howto__screenshot">
                  <img
                    src="/guide-screenshots/recipe-hub.png"
                    alt="レシピハブ"
                    className="howto__screenshot-image"
                    onError={(e) => e.target.parentElement.style.display = 'none'}
                  />
                </div>
                <ol className="howto__steps-list">
                  <li>レシピハブで「🧊 今ある食材でレシピ」をタップ</li>
                  <li>「📷 冷蔵庫を撮影」または「🔘 食材を選ぶ」</li>
                  <li>AIが食材を認識 → レシピを生成（10pt）</li>
                  <li>生成されたレシピは自動保存されます</li>
                </ol>
                <button
                  className="howto__action-btn"
                  onClick={() => navigate('/recipe-hub')}
                >
                  レシピハブを開く →
                </button>
              </div>

              {/* 特売品でレシピ */}
              <div className="howto__card" id="feat-shopping">
                <h2 className="howto__heading">🛒 特売品でレシピ</h2>
                <p className="howto__text">
                  スーパーのチラシや特売品の写真から、節約レシピを提案します。橋本病にも配慮した内容です。
                </p>
                <ol className="howto__steps-list">
                  <li>レシピハブで「🛒 特売品でレシピ」をタップ</li>
                  <li>チラシや特売品の写真を撮影</li>
                  <li>AIが特売品を認識 → 節約レシピを提案（10pt）</li>
                </ol>
                <button
                  className="howto__action-btn"
                  onClick={() => navigate('/recipe-hub')}
                >
                  チラシレシピを試す →
                </button>
              </div>

              {/* 商品の成分表チェック */}
              <div className="howto__card" id="feat-check">
                <h2 className="howto__heading">🔍 商品の成分表チェック</h2>
                <p className="howto__text">
                  商品の成分表を撮影するだけ。ヨウ素・添加物など、橋本病への影響を自動で分析します。
                </p>
                <ol className="howto__steps-list">
                  <li>レシピハブで「🔍 商品成分表チェック」をタップ</li>
                  <li>商品の成分表を撮影（5pt）</li>
                  <li>橋本病への影響が表示されます</li>
                </ol>
                <p className="howto__caution">
                  ※ あくまで参考情報です。気になる場合は医師に相談してください。
                </p>
                <button
                  className="howto__action-btn"
                  onClick={() => navigate('/ingredient-check')}
                >
                  成分チェックを使う →
                </button>
              </div>

              {/* おやすみモード */}
              <div className="howto__card" id="feat-rest">
                <h2 className="howto__heading">🌙 おやすみモード</h2>
                <p className="howto__text">
                  体調がつらい日に。画面がシンプルになり、負担を減らします。
                </p>
                <ul className="howto__tips-list">
                  <li>✅ 画面表示がシンプルに</li>
                  <li>✅ 簡単レシピを優先表示</li>
                  <li>✅ 必要な機能だけ表示</li>
                </ul>
                <ol className="howto__steps-list">
                  <li>マイページを開く</li>
                  <li>「🌙 おやすみモード」をタップ</li>
                  <li>ONにする（体調が戻ったら同じ手順でOFF）</li>
                </ol>
                <button
                  className="howto__action-btn"
                  onClick={() => navigate('/mypage')}
                >
                  おやすみモードを設定する →
                </button>
              </div>

            </div>
          )}

          {/* ===============================================
              タブ3: 困ったとき
              =============================================== */}
          {activeTab === 'help' && (
            <div className="howto__section">

              {/* つむぎに聞く導線 */}
              <div className="howto__help-quick">
                <p className="howto__help-quick-text">
                  💬 困ったら、つむぎに「使い方を教えて」と聞いてみてね
                </p>
                <button
                  className="howto__action-btn"
                  onClick={() => navigate('/chat?prompt=' + encodeURIComponent('使い方を教えて'))}
                >
                  つむぎに聞く →
                </button>
              </div>

              {/* こんな時どうする？ */}
              <div className="howto__card">
                <h2 className="howto__heading">😢 こんな時どうする？</h2>

                <div className="howto__sub-section">
                  <h3 className="howto__subheading">【体調が悪い】</h3>
                  <ol className="howto__steps-list">
                    <li>おやすみモードをONにする → マイページ「🌙 おやすみモード」</li>
                    <li>簡単レシピを見る → レシピハブ「🧊 今ある食材でレシピ」</li>
                    <li>つむぎに話を聞いてもらう → フッター「💬 チャット」</li>
                  </ol>
                </div>

                <div className="howto__sub-section">
                  <h3 className="howto__subheading">【レシピが難しすぎる】</h3>
                  <ol className="howto__steps-list">
                    <li>チャットで「もっと簡単にできますか？」と聞く</li>
                    <li>レシピ設定で調味料を少なくする</li>
                  </ol>
                </div>

                <div className="howto__sub-section">
                  <h3 className="howto__subheading">【アレルギー食材が含まれている】</h3>
                  <ol className="howto__steps-list">
                    <li>レシピ設定 → アレルギー設定が正しいか確認</li>
                    <li>設定を保存し直す → 以降は自動反映されます</li>
                  </ol>
                </div>
              </div>

              {/* ポイントについて */}
              <div className="howto__card">
                <h2 className="howto__heading">💎 ポイントについて</h2>
                <p className="howto__text">
                  毎月、プランに応じたポイントが付与されます。機能を使うとポイントを消費します。
                </p>

                <h3 className="howto__subheading">【ポイント消費】</h3>
                <table className="howto__table">
                  <thead>
                    <tr>
                      <th>機能</th>
                      <th>ポイント</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>つむぎチャット</td>
                      <td>1pt / 回</td>
                    </tr>
                    <tr>
                      <td>AI栄養相談（フォーム）</td>
                      <td>4pt / 回</td>
                    </tr>
                    <tr>
                      <td>レシピ生成</td>
                      <td>4pt / 回</td>
                    </tr>
                    <tr>
                      <td>成分表スキャン</td>
                      <td>5pt / 回</td>
                    </tr>
                    <tr>
                      <td>成分チェック</td>
                      <td>5pt / 回</td>
                    </tr>
                    <tr>
                      <td>血液検査分析</td>
                      <td>8pt / 回</td>
                    </tr>
                    <tr>
                      <td>冷蔵庫レシピ</td>
                      <td>10pt / 回</td>
                    </tr>
                    <tr>
                      <td>チラシレシピ</td>
                      <td>10pt / 回</td>
                    </tr>
                  </tbody>
                </table>

                <h3 className="howto__subheading">【プラン一覧】</h3>
                <table className="howto__table">
                  <thead>
                    <tr>
                      <th>プラン</th>
                      <th>月額</th>
                      <th>月間ポイント</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Free</td>
                      <td>0円</td>
                      <td>50pt</td>
                    </tr>
                    <tr>
                      <td>Standard</td>
                      <td>480円</td>
                      <td>900pt</td>
                    </tr>
                    <tr>
                      <td>Premium</td>
                      <td>980円</td>
                      <td>1,800pt</td>
                    </tr>
                    <tr>
                      <td>Premium Plus</td>
                      <td>1,980円</td>
                      <td>3,600pt</td>
                    </tr>
                    <tr>
                      <td>Ultimate</td>
                      <td>2,980円</td>
                      <td>5,500pt</td>
                    </tr>
                  </tbody>
                </table>

                <p className="howto__text">
                  ポイント残高はマイページで確認できます。足りなくなったらプラン変更もできるよ。
                </p>
              </div>

              {/* それでも困ったら */}
              <div className="howto__card">
                <h2 className="howto__heading">📮 それでも困ったら</h2>
                <p className="howto__text">
                  つむぎに「使い方を教えて」と聞くと、案内してくれます。それでも解決しない場合は、メニューの「❓ FAQ」も見てみてね。
                </p>
                <ul className="howto__tips-list">
                  <li>チャットで質問 →「○○の使い方を教えて」</li>
                  <li>FAQ → ハンバーガーメニュー「❓ FAQ」</li>
                  <li>お問い合わせ → ハンバーガーメニュー「📮 お問い合わせ」</li>
                </ul>
              </div>

              {/* CTA */}
              <div className="howto__cta">
                <h2 className="howto__cta-title">気が向いたら、話しかけてね</h2>
                <p className="howto__cta-text">
                  つむぎはいつでもここにいるよ
                </p>
                <div className="howto__cta-buttons">
                  <button
                    className="howto__button howto__button--primary"
                    onClick={() => navigate('/form-v2')}
                  >
                    📝 相談を始める
                  </button>
                  <button
                    className="howto__button howto__button--secondary"
                    onClick={() => navigate('/chat')}
                  >
                    💬 つむぎと話す
                  </button>
                </div>
              </div>

            </div>
          )}

        </div>
        <AdBannerResponsive />
        <ProductCarousel location="how-to-use" />
      </div>
    </AppLayout>
  );
};

export default HowToUsePage;
